import { Component } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { ActivatedRoute } from '@angular/router'
import { Store } from '@ngrx/store'
import { Observable, Subject, catchError, map, of } from 'rxjs'
import {
  DISPLAY_ID,
  DOCUMENT_NAMES,
  ENTITY_ID,
  DISPLAY_DESCRIPTION,
  TASK_DUE_DATE,
  CALENDAR_DISPLAY_FOR,
  CALENDAR_DISPLAY_TASK_FROM,
  CALENDAR_DISPLAY_TASK_TILL,
  SEND_NOTIFICATION
} from 'src/app/constants/excel.columns'
import { GlobalConstants } from 'src/app/constants/global-constants'
import { CalendarEventAPIModel, PaginatedAPI } from 'src/app/models/api.model'
import { CalendarService } from 'src/app/services/calendar/calendar.service'
import { CommonService, convertToISO, formatDatetoYYYYMMDD } from 'src/app/services/common/common.service'
import { getAllCalendarSummary } from 'src/app/store/selector/calendar.selector'
import { AppState } from 'src/app/store/state/app.state'
import { AbstractValidator, IColumn } from '../../import/validators/abstract-validator.interface'
import { futureAndCurrentDateAsyncValidator } from 'src/app/validators/validators.validator'

@Component({
  selector: 'app-create-calendar',
  templateUrl: './create-calendar.component.html',
  styleUrls: ['./create-calendar.component.scss']
})
export class CreateCalendarComponent {
  calendarEventForm: FormGroup
  calendarId: CalendarEventAPIModel
  calendarDetail: CalendarEventAPIModel
  calendarAllList$: Observable<PaginatedAPI<CalendarEventAPIModel>>
  filesToBeUploaded: FileList
  displayForEmployee: boolean = false
  displayForEmployer: boolean = false
  showImport: boolean = false
  importValidator: CalendarEventValidator

  constructor(
    private formBuilder: FormBuilder,
    private calendarService: CalendarService,
    private cs: CommonService,
    private route: ActivatedRoute,
    private appState: Store<AppState>
  ) {
    this.importValidator = new CalendarEventValidator(calendarService)
  }

  ngOnInit() {
    this.buildCalendarForm()
    this.calendarAllList$ = this.appState.select(getAllCalendarSummary())
    this.route.params.subscribe(params => {
      const id = params['id']
      this.calendarId = id
    })
    if (this.calendarId) {
      this.dispatchFetchCalendarDetailsAction(this.calendarId)
      this.getCalendarDetailsFromStore()
    }
  }

  dispatchFetchCalendarDetailsAction(id: CalendarEventAPIModel) {
    this.calendarService.dispatchFetchCalendarDetailsAction(id)
  }

  handleFileInput(files: FileList) {
    this.filesToBeUploaded = files
  }

  calendarFormPatch(data) {
    this.calendarEventForm.patchValue({
      displayDescription: data.displayDescription,
      displayFromDatetime: formatDatetoYYYYMMDD(data.displayFromDatetime),
      displayToDatetime: formatDatetoYYYYMMDD(data.displayToDatetime),
      dueDatetime: formatDatetoYYYYMMDD(data.dueDatetime)
    })

    this.displayForEmployee = data.displayFor.includes(GlobalConstants.EMPLOYEE)
    this.displayForEmployer = data.displayFor.includes(GlobalConstants.EMPLOYER)
  }

  getCalendarDetailsFromStore() {
    this.calendarService.getCalendarDetailsFromStore().subscribe(calendar => {
      if (calendar) {
        this.calendarDetail = calendar
        this.calendarFormPatch(this.calendarDetail)
      }
    })
  }

  buildCalendarForm() {
    this.calendarEventForm = this.formBuilder.group({
      displayDescription: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(150)]],
      displayFromDatetime: [
        '',
        { validators: [Validators.required], asyncValidators: [futureAndCurrentDateAsyncValidator] }
      ],
      displayToDatetime: ['', { validators: [this.dateGreaterThan('displayFromDatetime', 'displayToDatetime')] }],
      companyEntityId: ['', [Validators.required]],
      displayFor: [''],
      dueDatetime: ['', { validators: [Validators.required], asyncValidators: [futureAndCurrentDateAsyncValidator] }]
      // notify: [false]
    })
    this.calendarEventForm
      .get('displayFromDatetime')
      .valueChanges.subscribe(() => this.calendarEventForm.get('displayToDatetime').updateValueAndValidity())
  }

  handleCompanySelectedValue(companyEntityId: string) {
    this.calendarEventForm.patchValue({ companyEntityId })
  }

  clearSelectedFiles(flag: boolean) {
    if (flag) {
      this.filesToBeUploaded = null
    }
  }

  onCategorySelected(category: string) {
    this.calendarEventForm.patchValue({ category })
  }

  get f() {
    return this.calendarEventForm.controls
  }

  saveAndExit() {
    this.displaySelect()

    if (this.calendarEventForm.invalid) {
      this.calendarEventForm.markAllAsTouched()
      return
    }
    const files: File[] = Array.from(this.filesToBeUploaded ?? [])

    if (this.calendarEventForm.valid) {
      const calendarEvents = this.calendarEventForm.value
      if (this.calendarId) {
        this.calendarService.dispatchCalendarAction(
          this.calendarId,
          calendarEvents,
          files,
          this.cs.createRoutingURL([GlobalConstants.ROUTING.CALENDAR_INFO, GlobalConstants.ROUTING.SUMMARY])
        )
      } else {
        this.calendarService.dispatchSaveCalendartAction(
          calendarEvents,
          files,
          this.cs.createRoutingURL([GlobalConstants.ROUTING.CALENDAR_INFO, GlobalConstants.ROUTING.SUMMARY])
        )
      }
    }
  }

  displaySelectEmployee(event: any) {
    this.displayForEmployee = event.target.checked
  }

  displaySelectEmployer(event: any) {
    this.displayForEmployer = event.target.checked
  }

  displaySelect() {
    if (this.displayForEmployee && this.displayForEmployer) {
      this.calendarEventForm.patchValue({ displayFor: `${GlobalConstants.EMPLOYEE},${GlobalConstants.EMPLOYER}` })
    } else if (this.displayForEmployee) {
      this.calendarEventForm.patchValue({ displayFor: GlobalConstants.EMPLOYEE })
    } else if (this.displayForEmployer) {
      this.calendarEventForm.patchValue({ displayFor: GlobalConstants.EMPLOYER })
    }
  }

  exit() {
    this.cs.route(GlobalConstants.ROUTING.CALENDAR_INFO, GlobalConstants.ROUTING.SUMMARY)
  }

  dateGreaterThan(from: string, to: string) {
    return (group: FormGroup): { [key: string]: any } => {
      const f = this.calendarEventForm?.controls?.[from]
      const t = this.calendarEventForm?.controls?.[to]
      if (!f || !t || t.value == '' || f.value == '') {
        return null
      }
      const fromDate = new Date(f.value)
      const toDate = new Date(t.value)

      const selectedDateWithoutTime = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate())
      const todayWithoutTime = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate())

      if (todayWithoutTime < selectedDateWithoutTime) {
        return {
          invalid: true
        }
      }
      return {}
    }
  }
}

export class CalendarEventValidator extends AbstractValidator<CalendarEventAPIModel> {
  public override allRows: Subject<CalendarEventAPIModel[]> = new Subject<CalendarEventAPIModel[]>()
  public override title = 'Calendar'
  public override templateName: string = 'Calendar_Template.xlsx'
  public override templateURL: string = ''
  public override saveResponse: Subject<any> = new Subject<any>()
  public override _header: Map<string, IColumn> = new Map([
    ['Entity ID', ENTITY_ID],
    ['Display ID', DISPLAY_ID],
    ['Task Due Date', TASK_DUE_DATE],
    ['Display Description', DISPLAY_DESCRIPTION],
    ['Display for', CALENDAR_DISPLAY_FOR],
    ['Display task from', CALENDAR_DISPLAY_TASK_FROM],
    ['Display Task till', CALENDAR_DISPLAY_TASK_TILL],
    ['Send Notification', SEND_NOTIFICATION],
    ['Document Names', DOCUMENT_NAMES]
  ])

  public override document: { isRequired: boolean; showUpload: boolean; file$: Subject<File> } = {
    isRequired: false,
    showUpload: true,
    file$: new Subject<File>()
  }

  public override startImporting$: Subject<boolean> = new Subject<boolean>()
  public override apiResponse$: Subject<any> = new Subject<boolean>()
  private rowsToSave = []
  private documentToSave = null
  constructor(private calendarService: CalendarService, u: CalendarEventAPIModel = null) {
    super(u)

    this.allRows.subscribe(rowsToSave => {
      this.rowsToSave = rowsToSave
    })

    this.document.file$.subscribe((file: File) => {
      this.documentToSave = file
    })

    this.subscribeImportStart()
  }

  private subscribeImportStart() {
    this.startImporting$.subscribe(doStartImporting => {
      if (doStartImporting) {
        const calendarEvents = this.createCalendarEventsList()
        this.calendarService
          .invokeSaveBulkCalendarAPI(calendarEvents, this.documentToSave)
          .pipe(
            map((response: any) => {
              this.apiResponse$.next({ status: 1, response: { ...response, fileName: 'calendar_event.xlsx' } })
            }),
            catchError((error: any) => {
              this.apiResponse$.next({ status: 0, error })
              return of(null)
            })
          )
          .subscribe()
      }
    })
  }

  private createCalendarEventsList(): CalendarEventAPIModel[] {
    const calendarTasks = []
    for (const row of this.rowsToSave) {
      const calendar = {}
      for (const key of Object.keys(row)) {
        if (key) {
          if (key === DOCUMENT_NAMES.key && row[key]) {
            const documents = []
            if (row[key].includes(',')) {
              const documentSplits = row[key].split(',')
              for (const documentSplit of documentSplits) {
                documents.push(documentSplit.trim())
              }
            } else {
              documents.push(row[key].trim())
            }
            calendar[key] = documents
            continue
          }
          if (!row[key] && key === DOCUMENT_NAMES.key) {
            calendar[key] = []
          } else if (key === SEND_NOTIFICATION.key && row[key]) {
            const nofify = row[key].toLowerCase() === 'yes' ? true : false
            calendar[key] = nofify
          } else if (
            (key === TASK_DUE_DATE.key ||
              key === CALENDAR_DISPLAY_TASK_FROM.key ||
              key === CALENDAR_DISPLAY_TASK_TILL.key) &&
            row[key]
          ) {
            calendar[key] = convertToISO(row[key])
          } else {
            calendar[key] = row[key]
          }
        }
      }
      calendarTasks.push(calendar)
    }
    return calendarTasks
  }
}
